import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  Sparkles, MousePointerClick, Rocket, Smartphone, LayoutGrid, Share2,
  RefreshCw, Palette, Globe, ShieldCheck, Zap, ArrowRight,
} from "lucide-react";
import { MarketingLayout } from "@/components/MarketingLayout";

const FEATURES = [
  { icon: Sparkles, title: "Chat-based building", body: "Describe your site in plain language and get a complete, styled, working page in seconds — powered by Claude." },
  { icon: Smartphone, title: "Mobile live preview", body: "A full-screen, interactive preview. No pinching, no zooming, no horizontal scrolling to find a button." },
  { icon: MousePointerClick, title: "Tap-to-edit", body: "Tap any element in the preview and describe the change. The AI rewrites just that piece — nothing else." },
  { icon: Rocket, title: "One-tap deploy", body: "Publish to a real, live URL instantly via Netlify. Share it anywhere the moment it's ready." },
  { icon: LayoutGrid, title: "Purpose-built templates", body: "Portfolios, storefronts, booking pages, waitlists — tuned for the things people actually build on phones." },
  { icon: RefreshCw, title: "Iterative editing", body: "Keep refining forever. Every change is a quick message; your whole history stays with the project." },
  { icon: Share2, title: "Quick share", body: "Copy your live link or open it straight from your dashboard. Perfect for sending to clients on the go." },
  { icon: Palette, title: "Beautiful by default", body: "Every generated site is responsive, modern, and content-complete — never a blank wireframe." },
  { icon: ShieldCheck, title: "Yours to keep", body: "Your projects, your content, your live URLs. Manage everything from a clean mobile dashboard." },
];

export default function Features() {
  const navigate = useNavigate();
  return (
    <MarketingLayout>
      <section className="mx-auto max-w-6xl px-6 pt-14 pb-4 text-center md:pt-20">
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-ho-primary">Features</span>
        <h1 className="font-display mx-auto mt-4 max-w-3xl text-4xl font-black tracking-tighter md:text-6xl">
          Everything you need to ship, in your pocket
        </h1>
        <p className="mx-auto mt-5 max-w-xl text-lg text-ho-muted">
          HandsOn packs a full build-and-deploy workflow into a phone-native experience.
        </p>
      </section>

      <section className="mx-auto max-w-6xl px-6 py-12">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map((f, i) => (
            <motion.div key={f.title}
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ delay: (i % 3) * 0.06, duration: 0.4 }}
              className="rounded-3xl border border-black/5 bg-white p-6">
              <div className="grid place-items-center w-12 h-12 rounded-2xl bg-ho-primary/10">
                <f.icon className="w-6 h-6 text-ho-primary" />
              </div>
              <h3 className="font-display mt-4 text-lg font-bold">{f.title}</h3>
              <p className="mt-2 text-[15px] leading-relaxed text-ho-muted">{f.body}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-6 pb-8">
        <div className="grid gap-4 md:grid-cols-2">
          <div className="rounded-[32px] bg-ho-ink p-8 text-white md:p-10">
            <Globe className="w-8 h-8 text-white/60" />
            <h3 className="font-display mt-4 text-2xl font-bold">Real deploys, not screenshots</h3>
            <p className="mt-3 text-white/70">Your published site lives at a genuine URL you can share, bookmark, and hand to clients. It's a real website — because it is one.</p>
          </div>
          <div className="rounded-[32px] bg-ho-primary p-8 text-white md:p-10">
            <Zap className="w-8 h-8 text-white/80" fill="white" />
            <h3 className="font-display mt-4 text-2xl font-bold">Fast enough to feel like magic</h3>
            <p className="mt-3 text-white/80">First draft in under ten seconds. Edits land near-instantly. Momentum is the whole point.</p>
            <button onClick={() => navigate("/login")} data-testid="features-cta"
              className="tap-scale mt-6 inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-semibold text-ho-primary">
              Try it free <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
